/**
 * Guía de instalación de la PWA · detección de plataforma y elección de variante.
 *
 * Módulo liviano (va en el bundle inicial): solo lee el User-Agent. El guion (`scenes.ts`) y el
 * teléfono dibujado (`device-mockup.tsx`) se cargan en diferido con `React.lazy`.
 *
 * Reutiliza `device/platform.ts` (`browserEnv`, `isIos`, `isStandalone`) y le suma lo que la
 * guía necesita: navegador, iPad y versión mayor de iOS.
 *
 * Flujos verificados en septiembre de 2026 (fuentes en `docs/Brief_Guia_Instalacion_PWA.md` §3):
 * - iOS 26+ Safari: «···» → «Compartir» → deslizar → «Agregar a Inicio» → «Abrir como app web» →
 *   «Agregar». iOS 17–18: «Compartir» en la barra inferior → … → «Agregar».
 * - Chrome/Edge/Firefox en iOS 16.4+: «Compartir» dentro de la barra de direcciones, arriba.
 * - iPad: «Compartir» arriba a la derecha; la hoja se abre como popover.
 * - Android: `beforeinstallprompt` → botón propio; respaldo manual ⋮ → «Instalar y crear acceso
 *   directo» → «Instalar» (Firefox: «Agregar a la pantalla de inicio» → «Agregar»).
 *
 * Versión de iOS: desde iOS 26 Safari congela el sistema del UA en `18_6` (webkit.org/blog/17333);
 * la versión real sale del token `Version/26.x` y solo si no existe se usa `OS xx_x`.
 */
import { STAFF_PWA_OPTIONS } from '../../../pwa.config';
import { browserEnv, isIos, type PlatformEnv } from '../../device/platform';

/** Nombre de la app tal como lo instala el teléfono: el del manifest de Staff. */
export const APP_NAME: string = STAFF_PWA_OPTIONS.manifest.name;

/** Host real que se muestra en la barra de direcciones del mockup (nunca una constante). */
export function currentHost(): string {
  return globalThis.location?.host ?? '';
}

export type InstallPlatform = 'ios' | 'android' | 'other';
export type InstallBrowser = 'safari' | 'chrome' | 'edge' | 'firefox' | 'samsung' | 'other';

export interface DeviceInfo {
  platform: InstallPlatform;
  browser: InstallBrowser;
  isIPad: boolean;
  /** Versión mayor de iOS/Safari (26, 18, 17…), o `null` si no aplica o no se puede saber. */
  iosMajor: number | null;
  mobile: boolean;
}

export function detectDevice(env: PlatformEnv = browserEnv()): DeviceInfo {
  const ua = env.userAgent;
  const touch = (env.maxTouchPoints ?? 0) > 1;
  // El iPad se anuncia como Mac: la pista es "Macintosh" con pantalla táctil.
  const isIPad = /iPad/.test(ua) || (/Macintosh/.test(ua) && touch);
  const ios = /iPhone|iPod/.test(ua) || isIPad || isIos(env);
  const android = !ios && /Android/i.test(ua);
  const platform: InstallPlatform = ios ? 'ios' : android ? 'android' : 'other';

  let browser: InstallBrowser = 'other';
  if (platform === 'ios') {
    if (/CriOS/.test(ua)) {
      browser = 'chrome';
    } else if (/EdgiOS/.test(ua)) {
      browser = 'edge';
    } else if (/FxiOS/.test(ua)) {
      browser = 'firefox';
    } else if (/Safari/.test(ua)) {
      browser = 'safari';
    }
  } else if (/SamsungBrowser/.test(ua)) {
    browser = 'samsung';
  } else if (/EdgA?\//.test(ua)) {
    browser = 'edge';
  } else if (/Firefox\//.test(ua)) {
    browser = 'firefox';
  } else if (/Chrome\//.test(ua)) {
    browser = 'chrome';
  }

  let iosMajor: number | null = null;
  if (platform === 'ios') {
    const version = /Version\/(\d+)/.exec(ua);
    const os = /OS (\d+)[_.]\d+/.exec(ua);
    // Chrome/Edge iOS no traen `Version/`: con el SO congelado en 18_6 no se distingue 26+.
    iosMajor = version ? Number(version[1]) : os ? Number(os[1]) : null;
  }

  return { platform, browser, isIPad, iosMajor, mobile: platform !== 'other' };
}

export const VARIANT_IDS = ['ios26', 'ios18', 'ios-chrome', 'ipad', 'android'] as const;
export type VariantId = (typeof VARIANT_IDS)[number];

/** Variante de la animación para un dispositivo; iOS sin versión detectable → `ios26`. */
export function variantFor(info: DeviceInfo): VariantId {
  if (info.platform === 'android') {
    return 'android';
  }
  if (info.platform === 'ios') {
    if (info.isIPad) {
      return 'ipad';
    }
    if (info.browser !== 'safari') {
      return 'ios-chrome';
    }
    return (info.iosMajor ?? 26) >= 26 ? 'ios26' : 'ios18';
  }
  return 'ios26';
}

/** `?instalar=ios26|ios18|ios-chrome|ipad|android` fuerza una variante (QA y escritorio). */
export const FORCE_PARAM = 'instalar';

export function parseVariant(value: string | null | undefined): VariantId | null {
  return (VARIANT_IDS as readonly string[]).includes(value ?? '') ? (value as VariantId) : null;
}

/** Variante forzada en la URL actual, si la hay. */
export function forcedVariant(
  search: string = globalThis.location?.search ?? '',
): VariantId | null {
  return parseVariant(new URLSearchParams(search).get(FORCE_PARAM));
}

/** Tamaño de la pantalla del mockup (px): teléfono 196×404, iPad 316×222. */
export const PHONE = { W: 196, H: 404 } as const;
export const TABLET = { W: 316, H: 222 } as const;
/** Marco del teléfono alrededor de la pantalla (6 px por lado). */
export const FRAME = 12;

export function sizeOf(id: VariantId): { W: number; H: number } {
  return id === 'ipad' ? TABLET : PHONE;
}

/**
 * Escala del mockup para que quepa en pantallas bajas (brief §5):
 * `max(0.66, min(1, (alto - reserva) / (H + 12)))`. En iPad se ajusta al ancho.
 */
export function mockupScale(
  id: VariantId,
  viewportHeight: number,
  viewportWidth: number,
  reserve = 430,
): number {
  const { W, H } = sizeOf(id);
  if (id === 'ipad') {
    return Math.min(1, Math.max(0, viewportWidth - 48) / (W + FRAME));
  }
  return Math.max(0.66, Math.min(1, (viewportHeight - reserve) / (H + FRAME)));
}

/* ───────────── Apertura manual (Perfil, card de Inicio) ─────────────
 * Un `CustomEvent` en `window`: cualquier botón abre la hoja de `InstallHost` sin compartir estado. */
export const OPEN_EVENT = 'fermin.install.open';

export interface OpenGuideDetail {
  /** Variante forzada (p. ej. `android` desde "¿No funciona?"). */
  variant?: VariantId;
  /** Muestra directamente los pasos manuales aunque haya `beforeinstallprompt`. */
  manual?: boolean;
  /**
   * Se abre con el dispositivo ya registrado (Perfil): en iPhone, la app instalada no comparte
   * almacenamiento con Safari y pedirá una re-vinculación (vpp-protocol §8); se avisa.
   */
  enrolled?: boolean;
}

export function openInstallGuide(detail: OpenGuideDetail = {}): void {
  window.dispatchEvent(new CustomEvent<OpenGuideDetail>(OPEN_EVENT, { detail }));
}
